import { useState } from 'react'
import Styles from './ExportModal.module.scss'
import { Modal, StackModalProps, useModals } from 'almanac-ui/Modal'
import { useAuthOrUnregisteredUser } from 'hooks/useAuthOrUnregisteredUser'
import { ArticleMeta } from 'types'
import { ModalStateEnum } from './Types'
import { ExternalIntegrations } from './ExternalIntegrations/ExternalIntegrations'
import { ConnectWordPressModal } from './ExternalIntegrations/WordPress/ConnectWordPress'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { ExportToFile } from './ExportToFile'
import { useArticle } from 'hooks/queries/articles/useArticle'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

interface ExportModalProps extends StackModalProps {
  article: ArticleMeta
  showBackButton?: boolean
}

export interface ExportModalContainerProps extends Omit<ExportModalProps, 'article'> {
  articleId: string
  showBackButton?: boolean
}

const ExportModalContainer = ({ articleId, ...props }: ExportModalContainerProps) => {
  const { data } = useArticle(articleId)

  if (!data) return null

  return <ExportModal {...props} article={data.article} />
}

const ExportModal = ({ close, article, showBackButton = false }: ExportModalProps) => {
  const { user: auth } = useAuthOrUnregisteredUser()
  const { closeModal } = useModals()
  const [modalState, setModalState] = useState<ModalStateEnum>(ModalStateEnum.Export)
  const { isMobile } = useResponsiveDesign()

  const workspaceId = useCurrentWorkspaceId()

  const queryClient = useQueryClient()

  if (!auth?.id) return null

  const handleSuccessfulConnect = () => {
    queryClient.invalidateQueries(['externalIntegrations', workspaceId])
    setModalState(ModalStateEnum.Export)
  }

  if (modalState === ModalStateEnum.ConnectWordPress) {
    return (
      <ConnectWordPressModal
        onCancel={() => setModalState(ModalStateEnum.Export)}
        onSuccessfulConnect={handleSuccessfulConnect}
      />
    )
  }

  if (modalState === ModalStateEnum.ExportToFile) {
    return (
      <ExportToFile
        index={0}
        article={article}
        onCancel={() => setModalState(ModalStateEnum.Export)}
        onSuccess={close}
        close={() => setModalState(ModalStateEnum.Export)}
      />
    )
  }

  return (
    <Modal.Root size="sm">
      <Modal.Header
        title={
          <>
            {!isMobile && showBackButton && <Modal.BackButton onPress={closeModal} />}
            Where would you like to export?
          </>
        }
        onClose={closeModal}
      />

      <div className={Styles.modalContent} data-testid="export_modal_content">
        <ExternalIntegrations setModalState={setModalState} article={article} />
      </div>
    </Modal.Root>
  )
}

export { ExportModalContainer as ExportModal }
